import { api } from './api';

export type OverviewControlTopPattern = {
  pattern_label: string;
  incident_type?: string;
  camera_id?: string;
  device_id?: string;
  group_key?: string;
  pattern_key?: string;
  pattern_volume_count?: number;
  qualifying_entry_count?: number;
  buyer_surface?: 'recurring_hotspot' | 'classification_attention' | 'other' | string;
  action_scope_count?: number;
  open_count?: number;
  open_member_count?: number;
  linked_member_count?: number;
  closed_member_count?: number;
  active_days_in_window?: number;
  zone_id?: string;
  severity?: string;
  pattern_state?: string;
  pattern_state_label?: string;
  control_status?: string;
  control_status_label?: string;
  pattern_owner?: string | null;
  followup_owner?: string | null;
  followup_owner_explicit?: boolean | null;
  followup_next_review_at?: string | null;
  work_order_status?: string;
  evidence_status?: string;
  outcome_measurement_status?: string;
  finding_saved?: boolean;
  review_status?: string | null;
  corrective_action_status?: string | null;
  measurement_status?: string | null;
  signoff_status?: string | null;
  report_status?: string | null;
  finalization_status?: string | null;
  package_available?: boolean;
  link_to_pattern_review?: string;
  link_to_safety_report?: string | null;
  risk_pattern_review_url?: string | null;
  safety_case_url?: string | null;
  safety_reports_url?: string | null;
  safety_scan_workspace_url?: string | null;
  safety_case_id?: string | null;
  finding_id?: string | null;
  representative_incident_id?: string;
  latest_signed_scan_id?: string | null;
  latest_signed_scan_at?: string | null;
  active_draft_report_id?: string | null;
  latest_observation_at?: string | null;
  has_new_activity_since_latest_signed_scan?: boolean | null;
  timing?: {
    status?: string | null;
    window_label?: string | null;
    concentration_percent?: number | null;
    timezone?: string | null;
  } | null;
  scan_measurement_summary?: {
    source?: string | null;
    confirmed_episode_count?: number | null;
    representative_evidence_count?: number | null;
    usable_hours?: number | null;
    measurement_status?: string | null;
    operational_finding?: string | null;
  } | null;
};

export type OverviewControlSummary = {
  critical_risks_under_review: number;
  uncontrolled_exposure_patterns: number;
  exposure_patterns_needing_review?: number;
  classification_attention_count?: number;
  qualifying_entry_count_total?: number;
  unassigned_pattern_owners: number;
  followups_due: number;
  followup_action_gaps?: number;
  open_work_orders: number;
  linked_work_orders_open?: number;
  proof_media_limitations: number;
  evidence_proof_gaps?: number;
  outcome_measurements_missing: number;
  top_patterns: OverviewControlTopPattern[];
  classification_attention_patterns?: OverviewControlTopPattern[];
};

export type OverviewControlSummaryResponse = {
  ok: boolean;
  org_id?: string;
  site_id?: string;
  as_of?: string;
  lookback_hours?: number;
  lookback_hours_label?: string;
  membership_source?: string;
  control_summary?: OverviewControlSummary;
  site_view?: {
    summary?: {
      current_valid_scans?: number;
      signed_safety_control_scans?: number;
      scans_in_progress?: number;
      patterns_awaiting_scan?: number;
    };
    valid_scan_register?: OverviewControlTopPattern[];
    signed_scan_register?: OverviewControlTopPattern[];
    scans_in_progress?: OverviewControlTopPattern[];
    patterns_awaiting_scan?: OverviewControlTopPattern[];
  };
};

let inflightControlSummary: Promise<OverviewControlSummaryResponse> | null = null;
let inflightControlSummaryKey = '';

export function isTransientOverviewApiError(error: unknown): boolean {
  const msg =
    (error as { message?: string })?.message ||
    (error as { response?: { status?: number } })?.response?.status?.toString() ||
    '';
  const lower = String(msg).toLowerCase();
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 502 || status === 503 || status === 504) return true;
  return (
    lower.includes('502') ||
    lower.includes('503') ||
    lower.includes('504') ||
    lower.includes('bad gateway') ||
    lower.includes('gateway timeout') ||
    lower.includes('network error') ||
    lower.includes('timeout') ||
    lower.includes('timed out') ||
    lower.includes('econnaborted')
  );
}

export const overviewApi = {
  getControlSummary: async (params?: {
    hours?: number;
    top_limit?: number;
  }): Promise<OverviewControlSummaryResponse> => {
    const key = JSON.stringify(params || {});
    if (inflightControlSummary && inflightControlSummaryKey === key) {
      return inflightControlSummary;
    }
    inflightControlSummaryKey = key;
    inflightControlSummary = api
      .get<OverviewControlSummaryResponse>('/api/v1/overview/control-summary', {
        params: params || {},
        // Production control-summary often takes 10–40s; 45s was racing cold builds.
        timeout: 90000,
      })
      .then((response) => response.data)
      .finally(() => {
        inflightControlSummary = null;
        inflightControlSummaryKey = '';
      });
    return inflightControlSummary;
  },
};
